import React, { useCallback, useEffect, useRef } from 'react'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import {
  AuditLogRetention,
  EventRetentionRule,
  FileRetention,
  RetentionPolicy,
  RetentionResourceType,
  RetentionRuleType,
  RoomRetention,
  SessionRetention,
  TTLRetentionRule,
} from '../../../models/RetentionPolicy'
import {
  useCreateRetentionPolicyMutation,
  useGetRetentionPoliciesQuery,
  useLazyGetRetentionPolicyQuery,
  useUpdateRetentionPolicyMutation,
} from '../../../store/account/account'
import { Modal } from '../../../components/Modal/Modal'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { SelectDropdown } from '../../../components/SelectDropdown/SelectDropdown'
import { FormFeedback } from '../../../components/Form/FormFeedback'
import { Tooltip } from '../../../components/Tooltip/Tooltip'

export interface EditPolicyModalProps {
  policyId?: string
  show: boolean
  onHide: () => void
  auditLogQuotaAvailable?: boolean
}

type FormValues = {
  rule: TTLRetentionRule | EventRetentionRule
  resource: RoomRetention | SessionRetention | FileRetention | AuditLogRetention
}
export const CreateOrEditPolicyModal: React.FC<EditPolicyModalProps> = ({ policyId, show, onHide, auditLogQuotaAvailable = true }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: retentionPoliciesData } = useGetRetentionPoliciesQuery({ accountId }, { refetchOnMountOrArgChange: true })

  const defaultValues = {
    rule: {
      type: RetentionRuleType.TTL,
      config: {
        value: 30,
        unit: 'day',
      },
    },
    resource: {
      type: RetentionResourceType.SESSION,
      config: null,
    },
  }

  const [savePolicyById, { isLoading: isUpdating, isSuccess, reset: resetUpdate }] = useUpdateRetentionPolicyMutation()
  const [createPolicy, { isLoading: isCreating, isSuccess: isCreated, reset: resetCreate }] = useCreateRetentionPolicyMutation()
  const hasHandledSuccessRef = useRef(false)
  const getRuleInHours = (rule: TTLRetentionRule | EventRetentionRule): TTLRetentionRule | EventRetentionRule => {
    if (rule.type === RetentionRuleType.EVENT) {
      return rule
    }
    rule = rule as TTLRetentionRule
    if (rule.config.unit === 'day') {
      return { ...rule, config: { unit: 'hour', value: 24 * rule.config.value } } as TTLRetentionRule
    }

    if (rule.config.unit === 'week') {
      return { ...rule, config: { unit: 'hour', value: 7 * rule.config.value * 24 } } as TTLRetentionRule
    }
    return rule
  }

  const form = useForm<FormValues>({ defaultValues })
  const { handleSubmit, setError } = form

  const isDefaultPolicy = (policy: RetentionPolicy | undefined): boolean =>
    policy?.isDefault === true || (policy as { is_default?: boolean })?.is_default === true

  const handleSave = async (data: FormValues) => {
    try {
      if (data.resource.type === RetentionResourceType.AUDIT_LOG) {
        const auditLogKind = (data.resource as AuditLogRetention).config?.kind
        const existingPolicy = retentionPoliciesData?.data?.find(
          (policy) => policy.resource.type === RetentionResourceType.AUDIT_LOG && (policy.resource as AuditLogRetention).config?.kind === auditLogKind,
        )

        if (auditLogKind === 'archived_log' && !policyId) {
          const searchableLogExists = retentionPoliciesData?.data?.some(
            (policy) =>
              policy.resource.type === RetentionResourceType.AUDIT_LOG &&
              (policy.resource as AuditLogRetention).config?.kind === 'searchable_log' &&
              (policy.resource as AuditLogRetention).config?.defaultEnabled !== false,
          )

          if (!searchableLogExists) {
            setError('resource.config.kind', {
              type: 'manual',
              message: 'Searchable audit log policy must exist before creating archived audit log policy.',
            })
            return
          }
        }

        const userRuleForValidation = data.rule as TTLRetentionRule
        let ttlInDays: number

        if (userRuleForValidation.config.unit === 'hour') {
          ttlInDays = userRuleForValidation.config.value / 24
        } else if (userRuleForValidation.config.unit === 'day') {
          ttlInDays = userRuleForValidation.config.value
        } else if (userRuleForValidation.config.unit === 'week') {
          ttlInDays = userRuleForValidation.config.value * 7
        } else {
          ttlInDays = 0
        }

        if (auditLogKind === 'searchable_log') {
          if (ttlInDays < 1) {
            setError('rule.config.value', {
              type: 'manual',
              message: 'Searchable logs retention period must be at least 1 day.',
            })
            return
          }

          if (ttlInDays > 90) {
            setError('rule.config.value', {
              type: 'manual',
              message: 'Searchable logs retention period must not exceed 90 days.',
            })
            return
          }
        } else if (auditLogKind === 'archived_log') {
          if (ttlInDays < 90) {
            setError('rule.config.value', {
              type: 'manual',
              message: 'Archived logs retention period must be at least 90 days.',
            })
            return
          }

          if (ttlInDays > 365) {
            setError('rule.config.value', {
              type: 'manual',
              message: 'Archived logs retention period must not exceed 365 days.',
            })
            return
          }
        }

        const userRule = getRuleInHours(data.rule)

        if (policyId) {
          await savePolicyById({
            accountId,
            policyId,
            body: {
              rule: userRule,
              resource: {
                ...data.resource,
                config: { ...(data.resource as AuditLogRetention).config, defaultEnabled: null },
              } as AuditLogRetention,
            },
          }).unwrap()
        } else if (existingPolicy) {
          if (isDefaultPolicy(existingPolicy)) {
            await createPolicy({
              accountId,
              body: {
                rule: userRule,
                resource: {
                  ...data.resource,
                  config: { ...(data.resource as AuditLogRetention).config, defaultEnabled: null },
                } as AuditLogRetention,
              },
            }).unwrap()
          } else {
            await savePolicyById({
              accountId,
              policyId: existingPolicy.id,
              body: {
                rule: userRule,
                resource: {
                  ...data.resource,
                  config: { ...(data.resource as AuditLogRetention).config, defaultEnabled: null },
                } as AuditLogRetention,
              },
            }).unwrap()
          }
        } else {
          await createPolicy({
            accountId,
            body: {
              rule: userRule,
              resource: {
                ...data.resource,
                config: { ...(data.resource as AuditLogRetention).config, defaultEnabled: null },
              } as AuditLogRetention,
            },
          }).unwrap()
        }
        form.reset()
        return
      }

      // Handle non-audit-log policies (existing behavior)
      if (policyId) {
        await savePolicyById({
          accountId,
          policyId,
          body: {
            rule: getRuleInHours(data.rule),
            resource: data.resource,
          },
        }).unwrap()
      } else {
        await createPolicy({
          accountId,
          body: {
            rule: getRuleInHours(data.rule),
            resource: data.resource,
          },
        }).unwrap()
        form.reset()
      }
    } catch {
      // Intentionally ignore save errors; UX currently doesn't surface them.
    }
  }

  useEffect(() => {
    if ((isSuccess || isCreated) && !hasHandledSuccessRef.current && show) {
      hasHandledSuccessRef.current = true
      onHide()
      setTimeout(() => {
        form.reset()
      }, 100)
    }
  }, [isSuccess, isCreated, onHide, form, show])

  useEffect(() => {
    if (!show) {
      form.reset()
      hasHandledSuccessRef.current = false
      resetUpdate()
      resetCreate()
    } else {
      hasHandledSuccessRef.current = false
    }
  }, [show, form, resetUpdate, resetCreate])

  const body = (
    <Container>
      <Row className='pb-3'>
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>{policyId ? 'Edit' : 'Create'} Data Retention Policy</h4>
        </Col>
      </Row>
      <Row>
        <FormProvider {...form}>
          <EditPolicyModalForm policyId={policyId} show={show} onHide={onHide} auditLogQuotaAvailable={auditLogQuotaAvailable} />
        </FormProvider>
      </Row>
    </Container>
  )

  return (
    <Modal
      show={show}
      onHide={onHide}
      header={<></>}
      body={body}
      showFooterActions={true}
      onFooterPrimaryAction={handleSubmit(handleSave)}
      footerPrimaryActionLoading={policyId ? isUpdating : isCreating}
      centered={true}
    />
  )
}

const EditPolicyModalForm: React.FC<EditPolicyModalProps> = ({ policyId, auditLogQuotaAvailable = true }) => {
  const { control, setValue, watch } = useFormContext<FormValues>()
  const resource = watch('resource')
  const accountId = useAppSelector(selectAccountId)
  const { data: retentionPoliciesData } = useGetRetentionPoliciesQuery({ accountId }, { refetchOnMountOrArgChange: true })

  const searchableLogExists = retentionPoliciesData?.data?.some(
    (policy) =>
      policy.resource.type === RetentionResourceType.AUDIT_LOG &&
      (policy.resource as AuditLogRetention).config?.kind === 'searchable_log' &&
      (policy.resource as AuditLogRetention).config?.defaultEnabled !== false,
  )

  const ruleTypes = [{ id: 'ttl', label: 'Time-to-live' }]
  const ttlRuleUnits = [
    { id: 'hour', label: 'Hour(s)' },
    { id: 'day', label: 'Day(s)' },
    { id: 'week', label: 'Weeks(s)' },
  ]
  const resourceTypes = [
    { id: 'file', label: 'File' },
    { id: 'session', label: 'Meeting' },
    ...(auditLogQuotaAvailable ? [{ id: 'audit_log', label: 'Audit Log' }] : []),
  ]
  const fileKinds = [
    { id: 'attachment', label: 'Attachment' },
    { id: 'recording', label: 'Meeting' },
    { id: 'transcription', label: 'Transcription' },
  ]
  const auditLogKinds = [
    { id: 'searchable_log', label: 'Searchable Log' },
    ...(searchableLogExists || policyId ? [{ id: 'archived_log', label: 'Archived Log' }] : []),
  ]

  const [getRetentionPolicy, getRetentionPolicyResult] = useLazyGetRetentionPolicyQuery()

  useEffect(() => {
    if (policyId) {
      getRetentionPolicy({ accountId, policyId })
    }
  }, [accountId, getRetentionPolicy, policyId])

  const resetResourceConfig = useCallback(() => {
    setValue('resource.config.kind', null)
    setValue('resource.config.source', null)
  }, [setValue])

  useEffect(() => {
    if (getRetentionPolicyResult?.data && getRetentionPolicyResult.isSuccess) {
      const { data: retentionPolicy } = getRetentionPolicyResult.data
      setValue('rule.type', retentionPolicy.rule.type ?? '')
      setValue('resource.type', retentionPolicy.resource.type ?? '')
      if (retentionPolicy.rule.type === RetentionRuleType.TTL) {
        setValue('rule.config.unit', (retentionPolicy.rule as TTLRetentionRule).config.unit ?? null)
        setValue('rule.config.value', (retentionPolicy.rule as TTLRetentionRule).config.value ?? null)
      }
      if (retentionPolicy.resource.type === RetentionResourceType.FILE) {
        setValue('resource.config.kind', (retentionPolicy.resource as FileRetention).config?.kind ?? null)
      } else if (retentionPolicy.resource.type === RetentionResourceType.AUDIT_LOG) {
        setValue('resource.config.kind', (retentionPolicy.resource as AuditLogRetention).config?.kind ?? null)
      } else {
        resetResourceConfig()
      }
    }
  }, [getRetentionPolicyResult, resetResourceConfig, setValue])

  return (
    <Container className='d-flex flex-column gap-4 p-0'>
      {getRetentionPolicyResult.isFetching ? (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      ) : (
        <>
          <Row>
            <Col>
              <Controller
                name='rule.type'
                control={control}
                rules={{
                  required: 'Rule type is required.',
                }}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>
                      Retention Unit*
                      <Tooltip
                        className='zocks-text-gray-500'
                        placement='right'
                        title='⏳ Time-to-Live (TTL) is like an expiration date for your information. It defines how long we store your data in our systems. Once the TTL expires, your data is automatically deleted.️'
                      >
                        <i className='bi bi-info-circle' />
                      </Tooltip>
                    </Form.Label>
                    <SelectDropdown
                      className='zocks-px-0.75 py-2'
                      options={ruleTypes}
                      initialSelectedOption={ruleTypes.find((option) => option.id === field.value)}
                      onOptionSelected={(option) => field.onChange(option.id)}
                    />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='resource.type'
                control={control}
                rules={{
                  required: 'Resource type is required.',
                }}
                render={({ field }) => (
                  <>
                    <Form.Label>Resource Type*</Form.Label>
                    <SelectDropdown
                      className='zocks-px-0.75 py-2'
                      options={resourceTypes}
                      initialSelectedOption={resourceTypes.find((option) => option.id === field.value)}
                      onOptionSelected={(option) => {
                        if (option.id === RetentionResourceType.SESSION) {
                          resetResourceConfig()
                        } else if (option.id === RetentionResourceType.FILE) {
                          setValue('resource.config.kind', fileKinds[0].id)
                        } else if (option.id === RetentionResourceType.AUDIT_LOG) {
                          setValue('resource.config.kind', auditLogKinds[0].id)
                        }
                        field.onChange(option.id)
                      }}
                    />
                  </>
                )}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              {resource.type === 'file' && (
                <div className='field'>
                  <span className='p-float-label'>
                    <Controller
                      name='resource.config.kind'
                      control={control}
                      rules={{}}
                      render={({ field }) => (
                        <>
                          <Form.Label>Resource Type*</Form.Label>
                          <SelectDropdown
                            className='zocks-px-0.75 py-2'
                            options={fileKinds}
                            initialSelectedOption={fileKinds.find((option) => option.id === field.value)}
                            onOptionSelected={(option) => field.onChange(option.id)}
                          />
                        </>
                      )}
                    />
                  </span>
                </div>
              )}
              {resource.type === 'audit_log' && (
                <>
                  <div className='field'>
                    <span className='p-float-label'>
                      <Controller
                        name='resource.config.kind'
                        control={control}
                        rules={{}}
                        render={({ field, fieldState }) => (
                          <>
                            <Form.Label>Audit Log Type*</Form.Label>
                            <SelectDropdown
                              className='zocks-px-0.75 py-2'
                              options={auditLogKinds}
                              initialSelectedOption={auditLogKinds.find((option) => option.id === field.value)}
                              onOptionSelected={(option) => field.onChange(option.id)}
                            />
                            {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                          </>
                        )}
                      />
                    </span>
                  </div>
                  {resource.config?.kind && (
                    <div className='mt-2'>
                      <span className='zocks-text-gray-900 small'>
                        {resource.config.kind === 'searchable_log' ? (
                          <>Query logs instantly. Retention: 1-90 days.</>
                        ) : (
                          <>Long-term storage. Request from Support. Retention: 90–365 days.</>
                        )}
                      </span>
                    </div>
                  )}
                </>
              )}
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <Form.Label>
                TTL Value*
                <Tooltip
                  className='zocks-text-gray-500'
                  placement='right'
                  title='⏳ Time-to-Live Value (TTL Value) is the duration your information stays in our system. After this period, your information is automatically removed.'
                >
                  <i className='bi bi-info-circle' />
                </Tooltip>
              </Form.Label>
            </Col>
            <Col>
              <Controller
                name='rule.config.value'
                control={control}
                rules={{}}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Control type='number' {...field} min={1} />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
            <Col>
              <Controller
                name='rule.config.unit'
                control={control}
                rules={{}}
                render={({ field }) => (
                  <SelectDropdown
                    className='zocks-px-0.75 py-2'
                    options={ttlRuleUnits}
                    initialSelectedOption={ttlRuleUnits.find((option) => option.id === field.value)}
                    onOptionSelected={(option) => field.onChange(option.id)}
                  />
                )}
              />
            </Col>
          </Row>
        </>
      )}
    </Container>
  )
}
